import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Mail, Phone, ArrowRight } from "lucide-react";
import { SiteShell } from "./SiteShell";
import { SiteContactForm } from "./SiteContactForm";
import { POLES, SITE, poleT } from "../../lib/sitePoles";
import { useLang } from "../../lib/siteLang";

const TX = {
  fr: { all: "Tous nos pôles", pole: "PÔLE", ask: "Une demande concernant ce pôle ?", next: "Pôle suivant :", cLead: "Un projet, une question, une demande de devis ? Écrivez-nous ou appelez-nous : nous vous répondons rapidement.", phone: SITE.phone,
    legal: "Mentions légales", ed: "ÉDITEUR DU SITE", edP: "Solutions PRIME — société en cours de création", seat: "Siège social", tel: "Téléphone", dir: "DIRECTEUR DE LA PUBLICATION", dirP: "Le représentant légal de Solutions PRIME.",
    host: "HÉBERGEMENT", ip: "PROPRIÉTÉ INTELLECTUELLE", ipP: "L'ensemble des contenus du site (textes, logos, illustrations, marque XALTIX) est protégé. Toute reproduction sans autorisation écrite préalable est interdite. © 2026 SOLUTIONS PRIME — Tous droits réservés.",
    data: "DONNÉES PERSONNELLES", dataP: (e) => `Les informations transmises via le formulaire de contact sont utilisées uniquement pour répondre à votre demande et conservées 3 ans maximum. Vous pouvez exercer vos droits d'accès, de rectification et de suppression en écrivant à ${e}. Réclamation possible auprès de la CNIL (www.cnil.fr).` },
  en: { all: "All our services", pole: "SERVICE", ask: "A request about this service?", next: "Next service:", cLead: "A project, a question, a quote request? Write to us or call us: we will get back to you quickly.", phone: "+33 7 63 10 69 73",
    legal: "Legal notice", ed: "SITE PUBLISHER", edP: "Solutions PRIME — company being incorporated", seat: "Registered office", tel: "Phone", dir: "PUBLICATION DIRECTOR", dirP: "The legal representative of Solutions PRIME.",
    host: "HOSTING", ip: "INTELLECTUAL PROPERTY", ipP: "All content on this site (texts, logos, illustrations, XALTIX brand) is protected. Any reproduction without prior written permission is prohibited. © 2026 SOLUTIONS PRIME — All rights reserved.",
    data: "PERSONAL DATA", dataP: (e) => `Information sent through the contact form is used only to answer your request and kept for a maximum of 3 years. You may exercise your rights of access, rectification and deletion by writing to ${e}. Complaints may be lodged with the CNIL, the French data protection authority (www.cnil.fr).` },
};

export function SitePole() {
  const { slug } = useParams();
  const L = useLang();
  const T = TX[L];
  const i = POLES.findIndex((p) => p.slug === slug);
  if (i < 0) return <Navigate to="/accueil" replace />;
  const p = poleT(POLES[i], L);
  const next = poleT(POLES[(i + 1) % POLES.length], L);
  return (
    <SiteShell title={p.title}>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 grid lg:grid-cols-2 gap-12 items-center" data-testid={`site-pole-page-${p.slug}`}>
        <div>
          <Link to="/accueil#poles" className="inline-flex items-center gap-2 text-sm text-[#F6D88A] hover:underline" data-testid="site-pole-back"><ArrowLeft className="h-4 w-4" />{T.all}</Link>
          <p className="sp-reveal sp-h text-xs text-[#D9A441] mt-8">{T.pole} {String(i + 1).padStart(2, "0")}</p>
          <h1 className="sp-reveal sp-h text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4"><span className="sp-gold">{p.title}</span></h1>
          <p className="sp-reveal mt-6 text-base sm:text-lg leading-relaxed">{p.short}</p>
          <ul className="mt-8 space-y-3">
            {p.points.map((x, k) => <li key={x} className="sp-reveal flex gap-3" style={{ transitionDelay: `${k * 80}ms` }}><span className="h-6 w-6 rounded-full grid place-items-center bg-[#D9A441]/15 text-[#F6D88A] shrink-0"><Check className="h-3.5 w-3.5" /></span><span className="text-white/90">{x}</span></li>)}
          </ul>
        </div>
        <div className="sp-reveal sp-card rounded-3xl overflow-hidden"><img src={`/site/${p.img}.jpg`} alt={p.title} className="w-full h-full object-cover" /></div>
      </section>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 mt-24">
        <h2 className="sp-reveal sp-h text-base md:text-lg mb-8">{T.ask}</h2>
        <div className="sp-reveal"><SiteContactForm key={p.slug} pole={POLES[i].title} /></div>
        <Link to={`/pole/${next.slug}`} data-testid="site-pole-next" className="sp-reveal mt-12 flex items-center justify-end gap-2 text-[#F6D88A] font-semibold">{T.next} {next.title} <ArrowRight className="h-4 w-4" /></Link>
      </section>
    </SiteShell>
  );
}

export function SiteContact() {
  const T = TX[useLang()];
  return (
    <SiteShell title="Contact">
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 grid lg:grid-cols-[.8fr_1.2fr] gap-12" data-testid="site-contact-page">
        <div>
          <h1 className="sp-reveal sp-h text-4xl sm:text-5xl"><span className="sp-gold">Contact</span></h1>
          <p className="sp-reveal mt-6 leading-relaxed">{T.cLead}</p>
          <div className="mt-10 space-y-4">
            <a href={`mailto:${SITE.email}`} className="sp-card sp-reveal rounded-xl p-5 flex items-center gap-4" data-testid="site-contact-email-link"><Mail className="h-6 w-6 text-[#F6D88A]" /><span className="text-white font-semibold">{SITE.email}</span></a>
            <a href={`tel:${SITE.phoneHref}`} className="sp-card sp-reveal rounded-xl p-5 flex items-center gap-4" data-testid="site-contact-phone-link"><Phone className="h-6 w-6 text-[#F6D88A]" /><span className="text-white font-semibold">{T.phone}</span></a>
          </div>
        </div>
        <div className="sp-reveal"><SiteContactForm /></div>
      </section>
    </SiteShell>
  );
}

export function SiteLegal() {
  const S = ({ t, children }) => <section className="sp-reveal mt-10"><h2 className="sp-h text-sm text-[#F6D88A]">{t}</h2><div className="sp-line my-3 opacity-50" /><div className="leading-relaxed space-y-1">{children}</div></section>;
  const T = TX[useLang()];
  return (
    <SiteShell title={T.legal}>
      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-14" data-testid="site-legal-page">
        <h1 className="sp-h text-3xl sm:text-4xl"><span className="sp-gold">{T.legal}</span></h1>
        <S t={T.ed}><p>{T.edP}</p><p>{T.seat} : {SITE.address}</p><p>Email : {SITE.email} · {T.tel} : {T.phone}</p></S>
        <S t={T.dir}><p>{T.dirP}</p></S>
        <S t={T.host}><p>Emergent Labs Inc. — https://emergent.sh</p></S>
        <S t={T.ip}><p>{T.ipP}</p></S>
        <S t={T.data}><p>{T.dataP(SITE.email)}</p></S>
      </article>
    </SiteShell>
  );
}
